import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import AdminOrganization from "./AdminOrganizationPage";
import MembersTable from "./MembersTable";
import OrganizationApiKeysPage from "./OrganizationApiKeysPage";
import NavigationBar from "../../../components/NavBar/NavBar";
import { useDataContext } from "../../../utils/DataContext";
import { updateTokenUserCookie } from "../../../utils/cookiesUtils";
import { useNavigate } from "react-router-dom";
import "./OrganizationPage.css";
import { buttonText } from "../../../utils/buttonText";
import { pageText } from "../../../utils/pageText";

function OrganizationPage() {
  const { authReady, userData, userToken } = useDataContext();

  const [usersData, setUsersData] = useState(null);
  const [activeSection, setActiveSection] = useState("members");

  const navigate = useNavigate();

  const fetchUsersData = useCallback(async (organizationName) => {
    let url = "/auth/organization/list/users";
    if (organizationName) {
      url += `?organization=${organizationName}`;
    }
    axios
      .get(url)
      .then((response) => {
        setUsersData(response.data);
      })
      .catch((error) => {
        const errorMsg = error.response?.data || error.message;
        window.alert(errorMsg);
      });
  }, []);

  useEffect(() => {
    if (!authReady) return;
    if (!userToken) {
      navigate("/signin", { replace: true });
      return;
    }
    fetchUsersData();
    updateTokenUserCookie();
  }, [authReady, fetchUsersData, navigate, userToken]);

  if (!authReady || !userToken || !userData) return null;

  let display;
  if (usersData === null) {
    display = null;
  } else if (userData.role === "admin") {
    display = (
      <AdminOrganization
        fetchUsersData={fetchUsersData}
        usersData={usersData}
      />
    );
  } else if (
    userData.organization &&
    usersData.hasOwnProperty(userData.organization) &&
    usersData[userData.organization].hasOwnProperty(userData.email)
  ) {
    const organizationName = userData.organization;
    const membersTable = (
      <MembersTable
        members={usersData[organizationName]}
        organizationName={organizationName}
        fetchUsersData={fetchUsersData}
      />
    );
    display =
      userData.organization_role === "organizer" ? (
        <div className="organization-settings-layout">
          <div className="organization-settings-nav">
            <button
              type="button"
              className={`organization-settings-nav-button ${
                activeSection === "members" ? "active" : ""
              }`}
              onClick={() => setActiveSection("members")}
            >
              <svg
                className="organization-settings-nav-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              {buttonText("Members")}
            </button>
            <button
              type="button"
              className={`organization-settings-nav-button ${
                activeSection === "apiKeys" ? "active" : ""
              }`}
              onClick={() => setActiveSection("apiKeys")}
            >
              <svg
                className="organization-settings-nav-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
              </svg>
              {buttonText("API keys")}
            </button>
          </div>
          <div
            className={`organization-settings-main ${
              activeSection === "apiKeys" ? "api-keys-main" : ""
            }`}
          >
            <div className="organization-settings-hero">
              <h1 className="organization-settings-title">
                {pageText("Organization settings")}
              </h1>
              <p className="organization-settings-description">
                {pageText("Manage members, roles, and service credentials for")}{" "}
                <span className="organization-settings-name">
                  {organizationName}
                </span>
              </p>
            </div>
            {activeSection === "members" ? (
              membersTable
            ) : (
              <OrganizationApiKeysPage organizationName={organizationName} />
            )}
          </div>
        </div>
      ) : (
        <div className="organization-settings-layout">
          <div className="organization-settings-main">
            <div className="organization-settings-hero">
              <h1 className="organization-settings-title">
                {pageText("Organization members")}
              </h1>
              <p className="organization-settings-description">
                {pageText("View members and roles for")}{" "}
                <span className="organization-settings-name">
                  {organizationName}
                </span>
              </p>
            </div>
            {membersTable}
          </div>
        </div>
      );
  } else {
    display = (
      <div>
        <h1>{pageText("Your are not in any organization.")}</h1>
        <p>{pageText("Please let admin or organizer invite your to an organization.")}</p>
      </div>
    );
  }

  return (
    <div>
      <NavigationBar />
      <div className="organization-settings-page">{display}</div>
    </div>
  );
}

export default OrganizationPage;
